import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import { useHistory, useLocation } from "react-router-dom";

import {
  Button,
  Module,
  ModuleBody,
  ModuleHeader,
  Wrapper,
  InlineLoading,
} from "@wfp/ui";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMicroscope, faInfoCircle } from "@fortawesome/free-solid-svg-icons";

import Table from "components/CountryAdmin/CountryAdminContent/Table/Table";
import Filtering from "components/CountryAdmin/CountryAdminContent/Table/Filtering";
import {
  CountryAdminLabel,
  deliveryCategory,
  nonWfpCategory,
  purchaseDetailCategory,
} from "SCConstants";

import { getCurrentUser } from "data-handler/ducks/auth";
import { getIsAdmin } from "helpers/users";
import commoditiesDuck from "data-handler/ducks/commodities";
import { countriesDuck } from "data-handler/ducks/countries";
import {
  getIsStoreDoctorFetching,
  getIsStoreDoctorExportFetching,
  getIsEventsExportFetching,
  getStoreDoctorData,
  requestStoreDoctorData,
  requestStoreDoctorExport,
  requestEventsExport,
} from "data-handler/ducks/storeDoctor";
import {
  getXRayStatus,
  getXRayProgress,
  requestXRayTrigger,
  requestXRayCancel,
  resetXRay,
  getXRayError,
} from "data-handler/ducks/xray";
import {
  getBatchHealStatus,
  getBatchHealProgress,
  getBatchHealError,
  requestBatchHealTrigger,
  requestBatchHealCancel,
  resetBatchHeal,
} from "data-handler/ducks/batchHeal";
import {
  requestSchoolAdmins,
  getSchoolAdmins,
  getAdminLevelsIsFetching,
} from "data-handler/ducks/adminLevels";
import {
  HEALTH_CLEAN,
  HEALTH_UNCHECKED,
  healthCheckLabel,
  summariseStockHealth,
} from "../stockHealth";
import "./_content.scss";

// include_inactive widens the school-state predicate, it doesn't narrow scope
const hasScopingFilter = (filters) =>
  Object.keys(filters).some((key) => key !== "include_inactive");

const CountryAdminStocksTable = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const response = useSelector(getStoreDoctorData);
  const currentUser = useSelector(getCurrentUser);
  const storeDoctorFetching = useSelector(getIsStoreDoctorFetching);
  const exportFetching = useSelector(getIsStoreDoctorExportFetching);
  const eventsExportFetching = useSelector(getIsEventsExportFetching);
  const countries = useSelector(countriesDuck.getList);
  const countriesIsFetching = useSelector(countriesDuck.isFetching());
  const commodities = useSelector(commoditiesDuck.getList);
  const commoditiesFetching = useSelector(commoditiesDuck.isFetching());
  const schoolAdmins = useSelector(getSchoolAdmins);
  const adminLevelsFetching = useSelector(getAdminLevelsIsFetching);
  const initialFilters = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const filters = [];
    for (const [key, value] of params.entries()) {
      if (value) filters.push({ id: key, value });
    }
    return filters;
  }, [location.search]);

  // land on "issues only" by default - an explicit health param in a shared
  // URL (or a choice made later in this session) always wins over this
  const defaultFilters = useMemo(() => {
    if (initialFilters.some((f) => f.id === "health")) return initialFilters;
    return [...initialFilters, { id: "health", value: "sick" }];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filtersState, setFilters] = useState(defaultFilters);

  const [selectedCountryId, setSelectedCountryId] = useState(() => {
    const init = initialFilters.find((f) => f.id === "country_id");
    if (init) return init.value;
    return currentUser.is_admin ? "" : currentUser.country?.id || "";
  });

  const initialAdm1 = useMemo(() => {
    const init = initialFilters.find((f) => f.id === "adm_1");
    return init ? init.value : "";
  }, [initialFilters]);

  const xrayStatus = useSelector(getXRayStatus);
  const xrayProgress = useSelector(getXRayProgress);
  const xrayError = useSelector(getXRayError);

  const xrayBusy = xrayStatus === "triggering" || xrayStatus === "processing";

  const healStatus = useSelector(getBatchHealStatus);
  const healProgress = useSelector(getBatchHealProgress);
  const healError = useSelector(getBatchHealError);

  const healBusy = healStatus === "triggering" || healStatus === "processing";

  const anyBatchBusy = xrayBusy || healBusy;

  const [xrayInfoOpen, setXrayInfoOpen] = useState(false);
  const xrayInfoRef = useRef(null);

  useEffect(() => {
    if (!xrayInfoOpen) return;
    const handleClick = (e) => {
      if (xrayInfoRef.current && !xrayInfoRef.current.contains(e.target)) {
        setXrayInfoOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [xrayInfoOpen]);

  const buildFilters = useCallback(() => {
    const filters = {};
    filtersState.forEach(({ id, value }) => {
      if (value && value !== "") filters[id] = value;
    });
    return filters;
  }, [filtersState]);

  const confirmNoFilter = useCallback(
    (messageId, defaultMessage) =>
      window.confirm(intl.formatMessage({ id: messageId, defaultMessage })),
    [intl]
  );

  const handleXRayTrigger = useCallback(() => {
    const filters = buildFilters();
    if (
      !hasScopingFilter(filters) &&
      !confirmNoFilter(
        "CountryAdmin.xrayNoFilterWarning",
        "No filters applied. This will run X-ray on ALL stocks and may take a long time. Continue?"
      )
    )
      return;
    dispatch(requestXRayTrigger(filters));
  }, [dispatch, buildFilters, confirmNoFilter]);

  const handleXRayCancel = useCallback(() => {
    dispatch(requestXRayCancel());
  }, [dispatch]);

  const handleHealTrigger = useCallback(() => {
    const filters = buildFilters();
    if (
      !hasScopingFilter(filters) &&
      !confirmNoFilter(
        "CountryAdmin.healNoFilterWarning",
        "No filters applied. This will heal ALL stocks and may take a very long time. Continue?"
      )
    )
      return;
    dispatch(requestBatchHealTrigger(filters));
  }, [dispatch, buildFilters, confirmNoFilter]);

  const handleHealCancel = useCallback(() => {
    dispatch(requestBatchHealCancel());
  }, [dispatch]);

  const handleExportCsv = useCallback(() => {
    dispatch(requestStoreDoctorExport(filtersState));
  }, [dispatch, filtersState]);

  const hasIssuesFilter = filtersState.some(
    (f) => f.id === "issues" && f.value
  );

  const handleExportEventsCsv = useCallback(() => {
    dispatch(requestEventsExport(filtersState));
  }, [dispatch, filtersState]);

  const handleNeedsReviewNav = useCallback(() => {
    history.push("/countryAdmin/stock/needs-review");
  }, [history]);

  useEffect(() => {
    if (xrayStatus !== "completed" && xrayStatus !== "error") return;
    if (xrayStatus === "completed")
      loadPage(pageSize, pageNum, "", filtersState);
    const timer = setTimeout(() => dispatch(resetXRay()), 8000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [xrayStatus, dispatch]);

  useEffect(() => {
    if (healStatus !== "completed" && healStatus !== "error") return;
    if (healStatus === "completed")
      loadPage(pageSize, pageNum, "", filtersState);
    const timer = setTimeout(() => dispatch(resetBatchHeal()), 8000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [healStatus, dispatch]);

  const { count = 0, ids = [], hidden_inactive = 0 } = response;

  const data = ids.map((id) => response.results[id]);

  useEffect(() => {
    if (
      currentUser.is_admin &&
      countries.length === 0 &&
      !countriesIsFetching
    ) {
      dispatch(countriesDuck.fetchList());
    }
  }, [currentUser, countries, countriesIsFetching, dispatch]);

  useEffect(() => {
    if (commodities.length === 0 && !commoditiesFetching) {
      dispatch(commoditiesDuck.fetchList());
    }
  }, [dispatch, commodities, commoditiesFetching]);

  useEffect(() => {
    if (selectedCountryId) {
      dispatch(requestSchoolAdmins(1, selectedCountryId));
      dispatch(
        requestSchoolAdmins(2, selectedCountryId, initialAdm1 || undefined)
      );
    }
    loadPage(pageSize, 1, "", defaultFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const countryOptions = useMemo(
    () =>
      countries.map((country) => ({
        name: country.id,
        label: country.name,
      })),
    [countries]
  );

  const commodityOptions = useMemo(
    () =>
      commodities.map((commodity) => ({
        name: commodity.id,
        label: commodity.name,
      })),
    [commodities]
  );

  const adm1Options = useMemo(
    () => (schoolAdmins[1] || []).map((a) => ({ name: a.id, label: a.name })),
    [schoolAdmins]
  );

  const adm2Options = useMemo(
    () => (schoolAdmins[2] || []).map((a) => ({ name: a.id, label: a.name })),
    [schoolAdmins]
  );

  const adm1Label = "Admin 1";
  const adm2Label = "Admin 2";

  const filterFields = [
    {
      name: "stock_id",
      label: intl.formatMessage({
        id: "CountryAdmin.stockId",
        defaultMessage: "Stock ID",
      }),
    },
    {
      name: "school_id",
      label: intl.formatMessage({
        id: "CountryAdmin.schoolId",
        defaultMessage: "School ID",
      }),
    },
    {
      name: "school_name",
      label: CountryAdminLabel.find((e) => e.name === "countryAdmin.school")
        .label,
    },
    {
      name: "category",
      label: CountryAdminLabel.find(
        (e) => e.name === "countryAdmin.stockCategory"
      ).label,
      type: "select",
      options: [
        {
          name: "",
          label: intl.formatMessage({
            id: "countryAdmin.allCategories",
            defaultMessage: "All categories",
          }),
        },
        {
          name: deliveryCategory,
          label: intl.formatMessage({
            id: "countryAdmin.delivery",
            defaultMessage: "Delivery",
          }),
        },
        {
          name: purchaseDetailCategory,
          label: intl.formatMessage({
            id: "countryAdmin.purchase",
            defaultMessage: "Purchase",
          }),
        },
        {
          name: nonWfpCategory,
          label: intl.formatMessage({
            id: "countryAdmin.contribution",
            defaultMessage: "Contribution",
          }),
        },
      ],
    },
    {
      name: "commodity",
      label: CountryAdminLabel.find(
        (e) => e.name === "countryAdmin.stockCommodity"
      ).label,
      type: "select",
      options: [
        {
          name: "",
          label: intl.formatMessage({
            id: "countryAdmin.allCommodities",
            defaultMessage: "All commodities",
          }),
        },
        ...commodityOptions,
      ],
    },
    {
      name: "health",
      label: intl.formatMessage({
        id: "CountryAdmin.stockHealth",
        defaultMessage: "Health",
      }),
      type: "select",
      newRow: true,
      options: [
        {
          name: "",
          label: intl.formatMessage({
            id: "countryAdmin.allStocks",
            defaultMessage: "All stocks",
          }),
        },
        {
          name: "sick",
          label: intl.formatMessage({
            id: "countryAdmin.issuesOnly",
            defaultMessage: "Issues only",
          }),
        },
      ],
    },
    {
      name: "issue_unchained",
      label: "Unchained",
      type: "checkbox",
      visibleWhen: (state) => state.health === "sick",
    },
    {
      name: "issue_negative_balance",
      label: "Negative balance",
      type: "checkbox",
      visibleWhen: (state) => state.health === "sick",
    },
    {
      name: "issue_triple_gate",
      label: "Triple gate",
      type: "checkbox",
      visibleWhen: (state) => state.health === "sick",
    },
    {
      name: "issue_over_allocation",
      label: "Over allocation",
      type: "checkbox",
      visibleWhen: (state) => state.health === "sick",
    },
    {
      name: "issue_duplicate",
      label: "Duplicate",
      type: "checkbox",
      visibleWhen: (state) => state.health === "sick",
    },
    {
      name: "include_inactive",
      label: intl.formatMessage({
        id: "CountryAdmin.includeInactiveSchools",
        defaultMessage: "Include inactive schools",
      }),
      type: "checkbox",
      newRow: true,
    },
  ];

  if (currentUser.is_admin) {
    filterFields.unshift({
      name: "country_id",
      label: CountryAdminLabel.find((e) => e.name === "countryAdmin.country")
        .label,
      type: "select",
      options: [
        {
          name: "",
          label: intl.formatMessage({
            id: "countryAdmin.allCountries",
            defaultMessage: "All countries",
          }),
        },
        ...countryOptions,
      ],
    });
  }

  const admInsertIndex = currentUser.is_admin ? 1 : 0;
  filterFields.splice(
    admInsertIndex,
    0,
    {
      name: "adm_1",
      label: adm1Label,
      type: "select",
      disabled: adminLevelsFetching || !selectedCountryId,
      options: [
        {
          name: "",
          label: intl.formatMessage({
            id: "countryAdmin.allAdm1",
            defaultMessage: "All",
          }),
        },
        ...adm1Options,
      ],
    },
    {
      name: "adm_2",
      label: adm2Label,
      type: "select",
      disabled: adminLevelsFetching || !selectedCountryId,
      options: [
        {
          name: "",
          label: intl.formatMessage({
            id: "countryAdmin.allAdm2",
            defaultMessage: "All",
          }),
        },
        ...adm2Options,
      ],
    }
  );

  const filterDefaults = useMemo(() => {
    const defaults = filtersState.reduce(
      (a, x) => ({ ...a, [x.id]: x.value }),
      {}
    );
    // Expand comma-separated issues param into individual checkbox states
    if (defaults.issues) {
      defaults.issues.split(",").forEach((issue) => {
        defaults[`issue_${issue}`] = true;
      });
      delete defaults.issues;
    }
    return defaults;
  }, [filtersState]);

  const handleFieldChange = useCallback(
    (fieldName, value) => {
      setTimeout(() => {
        if (fieldName === "country_id") {
          setSelectedCountryId(value || "");
          if (value) {
            dispatch(requestSchoolAdmins(1, value));
            dispatch(requestSchoolAdmins(2, value));
          }
        } else if (fieldName === "adm_1") {
          if (selectedCountryId) {
            if (value) {
              dispatch(requestSchoolAdmins(2, selectedCountryId, value));
            } else {
              dispatch(requestSchoolAdmins(2, selectedCountryId));
            }
          }
        }
      }, 0);
    },
    [dispatch, selectedCountryId]
  );

  const filterProps = {
    fields: filterFields,
    onFieldChange: handleFieldChange,
    controlledResets: {
      country_id: ["adm_1", "adm_2"],
      adm_1: ["adm_2"],
      health: [
        "issue_unchained",
        "issue_negative_balance",
        "issue_triple_gate",
        "issue_over_allocation",
        "issue_duplicate",
      ],
    },
    onSubmit: (e) => {
      const issueKeys = [
        "issue_unchained",
        "issue_negative_balance",
        "issue_triple_gate",
        "issue_over_allocation",
        "issue_duplicate",
      ];
      const selectedIssues = issueKeys
        .filter((key) => e[key])
        .map((key) => key.replace("issue_", ""));

      const merged = {};
      Object.keys(e).forEach((key) => {
        if (!issueKeys.includes(key)) {
          merged[key] = e[key];
        }
      });
      if (selectedIssues.length > 0) {
        merged.issues = selectedIssues.join(",");
      }

      const filters = Object.keys(merged).map((key) => ({
        id: key,
        value: merged[key],
      }));
      setFilters(filters);
      const params = new URLSearchParams();
      filters.forEach(({ id, value }) => {
        if (value) params.set(id, value);
      });
      const qs = params.toString();
      history.replace({
        pathname: location.pathname,
        search: qs ? `?${qs}` : "",
      });
      loadPage(pageSize, 1, "", filters);
    },
    defaults: filterDefaults,
  };

  const columns = useMemo(() => {
    const allColumns = [
      {
        Header: intl.formatMessage({
          id: "CountryAdmin.stockId",
          defaultMessage: "Stock ID",
        }),
        accessor: "stock_id",
        Cell: ({ cell: { row, value } }) => {
          const stockId = row["original"]["stock_id"];

          return (
            <div className="stock-id-cell">
              {value}
              <a
                href={`/countryAdmin/stock/inspect/${stockId}`}
                className="stock-id-cell__inspect"
                title={intl.formatMessage({
                  id: "CountryAdmin.stockInspectCTA",
                  defaultMessage: "Inspect stock",
                })}
                onClick={(e) => {
                  if (
                    !e.ctrlKey &&
                    !e.metaKey &&
                    !e.shiftKey &&
                    e.button === 0
                  ) {
                    e.preventDefault();
                    e.stopPropagation();
                    history.push(`/countryAdmin/stock/inspect/${stockId}`);
                  }
                }}
              >
                <FontAwesomeIcon icon={faMicroscope} />
              </a>
            </div>
          );
        },
      },
      {
        Header: CountryAdminLabel.find(
          (e) => e.name === "countryAdmin.stockCommodity"
        ).label,
        accessor: "commodity_name",
      },
      {
        Header: CountryAdminLabel.find(
          (e) => e.name === "countryAdmin.stockCategory"
        ).label,
        accessor: "category",
        Cell: ({ cell: { row } }) =>
          row["original"]["category"].charAt(0).toUpperCase() +
          row["original"]["category"].slice(1),
      },
      {
        Header: CountryAdminLabel.find((e) => e.name === "countryAdmin.school")
          .label,
        accessor: "school_name",
        Cell: ({ cell: { row, value } }) => {
          const state = row["original"]["school_state"];
          if (!state || state === "active") return value;
          return (
            <span className="school-name-cell">
              {value}
              <span className="school-name-cell__state">{state}</span>
            </span>
          );
        },
      },
      {
        Header: intl.formatMessage({
          id: "CountryAdmin.stockHealth",
          defaultMessage: "Health",
        }),
        accessor: "health",
        Cell: ({ cell: { value } }) => {
          const { state, failing } = summariseStockHealth(value);
          if (state === HEALTH_UNCHECKED) {
            return (
              <span
                className="stock-health-badge stock-health-badge--unchecked"
                title="X-ray has never run on this stock"
              >
                ?
              </span>
            );
          }
          if (state === HEALTH_CLEAN) {
            return (
              <span className="stock-health-badge stock-health-badge--pass">
                ✓
              </span>
            );
          }
          return (
            <span
              className="stock-health-badge stock-health-badge--fail"
              title={failing.map(healthCheckLabel).join(", ")}
            >
              ✗ {failing.length}
            </span>
          );
        },
      },
    ];

    if (currentUser.is_admin) {
      allColumns.push({
        Header: CountryAdminLabel.find((e) => e.name === "countryAdmin.country")
          .label,
        accessor: "country_name",
      });
    }
    return allColumns;
  }, [currentUser.is_admin, history, intl]);

  const loadPage = (size, idx, ordering, filters = filtersState) => {
    setPageSize(size);
    setPageNum(idx);
    dispatch(requestStoreDoctorData(size, idx, ordering, filters));
  };

  const tableProps = {
    columns,
    data,
    pageNum,
    pageSize,
    totalItems: count,
    loadPage,
  };

  return (
    <div className="store-doctor-table">
      <Wrapper
        pageWidth="lg"
        spacing="md"
        background="lighter"
        className="table"
      >
        <Module>
          <ModuleHeader>
            <FormattedMessage id="countryAdmin.stock" defaultMessage="Stock" />
          </ModuleHeader>
          <ModuleBody>
            <div style={storeDoctorFetching ? { display: "none" } : {}}>
              <Filtering {...filterProps} />
              <div className="action-buttons-bar">
                <span className="xray-bar__info" ref={xrayInfoRef}>
                  <button
                    type="button"
                    className="xray-bar__info-btn"
                    onClick={() => setXrayInfoOpen((v) => !v)}
                    aria-label="X-ray info"
                  >
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </button>
                  {xrayInfoOpen && (
                    <div className="xray-bar__info-popover">
                      <FormattedMessage
                        id="CountryAdmin.xrayInfo"
                        defaultMessage="X-ray recalculates stock health by re-checking balances and chain integrity for the filtered stocks. Use filters to narrow the scope."
                      />
                    </div>
                  )}
                </span>
                <Button
                  small
                  kind="secondary"
                  disabled={anyBatchBusy}
                  onClick={handleXRayTrigger}
                >
                  <FormattedMessage
                    id="CountryAdmin.runXray"
                    defaultMessage="Run X-ray"
                  />
                </Button>
                <Button
                  small
                  kind="secondary"
                  disabled={anyBatchBusy}
                  onClick={handleHealTrigger}
                >
                  <FormattedMessage
                    id="CountryAdmin.runHeal"
                    defaultMessage="Run Heal"
                  />
                </Button>
                <Button
                  small
                  kind="secondary"
                  disabled={exportFetching}
                  onClick={handleExportCsv}
                >
                  {exportFetching ? (
                    <FormattedMessage
                      id="CountryAdmin.exportingCsv"
                      defaultMessage="Exporting…"
                    />
                  ) : (
                    <FormattedMessage
                      id="CountryAdmin.exportCsv"
                      defaultMessage="Export CSV"
                    />
                  )}
                </Button>
                {getIsAdmin(currentUser) && (
                  <Button
                    small
                    kind="secondary"
                    disabled={eventsExportFetching || !hasIssuesFilter}
                    onClick={handleExportEventsCsv}
                    title={
                      !hasIssuesFilter
                        ? "Select at least one issue type to export events"
                        : undefined
                    }
                  >
                    {eventsExportFetching ? (
                      <FormattedMessage
                        id="CountryAdmin.exportingEventsCsv"
                        defaultMessage="Exporting…"
                      />
                    ) : (
                      <FormattedMessage
                        id="CountryAdmin.exportEventsCsv"
                        defaultMessage="Export Events CSV"
                      />
                    )}
                  </Button>
                )}
                <Button small kind="secondary" onClick={handleNeedsReviewNav}>
                  <FormattedMessage
                    id="CountryAdmin.needsReviewNav"
                    defaultMessage="Needs review"
                  />
                </Button>
              </div>
              <div className="xray-bar">
                {xrayBusy && xrayProgress && (
                  <div className="xray-bar__progress">
                    <div className="xray-bar__track">
                      <div
                        className="xray-bar__fill"
                        style={{
                          width: `${
                            xrayProgress.total_stocks > 0
                              ? Math.round(
                                  (xrayProgress.completed_stocks /
                                    xrayProgress.total_stocks) *
                                    100
                                )
                              : 0
                          }%`,
                        }}
                      />
                    </div>
                    <span className="xray-bar__label">
                      {xrayProgress.completed_stocks} /{" "}
                      {xrayProgress.total_stocks}{" "}
                      <FormattedMessage
                        id="CountryAdmin.xrayNonEmptyStocks"
                        defaultMessage="non-empty stocks"
                      />
                      {xrayProgress.total_stocks_all != null && (
                        <> ({xrayProgress.total_stocks_all} total)</>
                      )}
                      {xrayProgress.total_schools > 0 && (
                        <>
                          {" · "}
                          {xrayProgress.completed_schools} /{" "}
                          {xrayProgress.total_schools}{" "}
                          <FormattedMessage
                            id="CountryAdmin.xraySchools"
                            defaultMessage="schools"
                          />
                        </>
                      )}
                    </span>
                  </div>
                )}
                {xrayBusy && (
                  <Button small kind="danger" onClick={handleXRayCancel}>
                    <FormattedMessage
                      id="CountryAdmin.xrayCancel"
                      defaultMessage="Cancel"
                    />
                  </Button>
                )}
                {xrayStatus === "triggering" && (
                  <InlineLoading
                    description={intl.formatMessage({
                      id: "CountryAdmin.xrayTriggering",
                      defaultMessage: "Starting X-ray…",
                    })}
                  />
                )}
                {xrayStatus === "completed" && (
                  <span className="xray-bar__done">
                    <FormattedMessage
                      id="CountryAdmin.xrayCompleted"
                      defaultMessage="X-ray completed"
                    />
                  </span>
                )}
                {xrayStatus === "cancelled" && (
                  <span className="xray-bar__cancelled">
                    <FormattedMessage
                      id="CountryAdmin.xrayCancelled"
                      defaultMessage="X-ray cancelled"
                    />
                  </span>
                )}
                {xrayStatus === "error" && xrayError && (
                  <span className="xray-bar__error">{xrayError}</span>
                )}
              </div>
              <div className="heal-bar">
                {healBusy && healProgress && (
                  <div className="heal-bar__progress">
                    <div className="heal-bar__track">
                      <div
                        className="heal-bar__fill"
                        style={{
                          width: `${
                            healProgress.total_stocks > 0
                              ? Math.round(
                                  (healProgress.completed_stocks /
                                    healProgress.total_stocks) *
                                    100
                                )
                              : 0
                          }%`,
                        }}
                      />
                    </div>
                    <span className="heal-bar__label">
                      {healProgress.completed_stocks} /{" "}
                      {healProgress.total_stocks}{" "}
                      <FormattedMessage
                        id="CountryAdmin.healStocks"
                        defaultMessage="stocks"
                      />
                      {healProgress.total_schools > 0 && (
                        <>
                          {" · "}
                          {healProgress.completed_schools} /{" "}
                          {healProgress.total_schools}{" "}
                          <FormattedMessage
                            id="CountryAdmin.healSchools"
                            defaultMessage="schools"
                          />
                        </>
                      )}
                    </span>
                  </div>
                )}
                {healBusy && (
                  <Button small kind="danger" onClick={handleHealCancel}>
                    <FormattedMessage
                      id="CountryAdmin.healCancel"
                      defaultMessage="Cancel"
                    />
                  </Button>
                )}
                {healStatus === "triggering" && (
                  <InlineLoading
                    description={intl.formatMessage({
                      id: "CountryAdmin.healTriggering",
                      defaultMessage: "Starting Heal…",
                    })}
                  />
                )}
                {healStatus === "completed" && (
                  <span className="heal-bar__done">
                    <FormattedMessage
                      id="CountryAdmin.healCompleted"
                      defaultMessage="Heal completed"
                    />
                  </span>
                )}
                {healStatus === "cancelled" && (
                  <span className="heal-bar__cancelled">
                    <FormattedMessage
                      id="CountryAdmin.healCancelled"
                      defaultMessage="Heal cancelled"
                    />
                  </span>
                )}
                {healStatus === "error" && healError && (
                  <span className="heal-bar__error">{healError}</span>
                )}
              </div>
              {hidden_inactive > 0 && (
                <div className="inactive-hidden-note">
                  <FormattedMessage
                    id="CountryAdmin.hiddenInactiveNote"
                    defaultMessage="{count, plural, one {# stock at an inactive school is hidden by the current filters. Tick include inactive schools to show them.} other {# stocks at inactive schools are hidden by the current filters. Tick include inactive schools to show them.}}"
                    values={{ count: hidden_inactive }}
                  />
                </div>
              )}
              <Table {...tableProps} showSort />
            </div>
            <div
              className="inlineLoading"
              style={!storeDoctorFetching ? { display: "none" } : {}}
            >
              <InlineLoading description="Loading..." />
            </div>
          </ModuleBody>
        </Module>
      </Wrapper>
    </div>
  );
};

export default CountryAdminStocksTable;
